import Vue from 'vue'
import VueI18n from 'vue-i18n'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, min, max, min_value, max_value, size, email, image } from 'vee-validate/dist/rules'
import { formatKB } from '~/utils/utils'
import { isObject } from '~/utils/v'

export default function ({ store }) {
  Vue.component('ValidationProvider', ValidationProvider)
  Vue.component('ValidationObserver', ValidationObserver)

  const locale = store.getters['lang/locale']

  const i18n = new VueI18n({
    locale,
    fallbackLocale: 'en',
    messages: {
      en: {
        validations: {
          code: 'en',
          messages: {
            alpha: 'The {} field may only contain alphabetic characters',
            alpha_num: 'The {} field may only contain alpha-numeric characters',
            alpha_dash: 'The {} field may contain alpha-numeric characters as well as dashes and underscores',
            alpha_spaces: 'The {} field may only contain alphabetic characters as well as spaces',
            between: 'The {} field must be between {min} and {max}',
            confirmed: 'The {} field confirmation does not match',
            digits: 'The {} field must be numeric and exactly contain {length} digits',
            dimensions: 'The {} field must be {width} pixels by {height} pixels',
            email: 'The {} field must be a valid email',
            phone: 'The {} field must be a valid phone',
            excluded: 'The {} field is not a valid value',
            ext: 'The {} field is not a valid file',
            image: 'The {} field must be an image',
            integer: 'The {} field must be an integer',
            length: 'The {} field must be {length} long',
            max_value: 'The {} field must be {max} or less',
            max: 'The {} field may not be greater than {length} characters',
            mimes: 'The {} field must have a valid file type',
            min_value: 'The {} field must be {min} or more',
            min: 'The {} field must be at least {length} characters',
            numeric: 'The {} field may only contain numeric characters',
            oneOf: 'The {} field is not a valid value',
            regex: 'The {} field format is invalid',
            required_if: 'The {} field is required',
            required: 'The {} field is required',
            size: 'The {} field size must be less than {size}',
            double: 'The {} field must be a valid decimal',
            count: {
              length: 'The number of objects should not exceed {length}.',
              isNotArray: 'The element being validated is not an array or object.'
            },
            password: {
              length: 'Your password must be at least {length} characters.',
              letters: 'Your password must contain at least one letter.',
              digit: 'Your password must contain at least one digit.'
            }
          }
        }
      },
      gr: {
        validations: {
          code: 'gr',
          messages: {
            alpha: 'Το πεδίο {} μπορεί να περιέχει μόνο αλφαβητικούς χαρακτήρες.',
            alpha_num: 'Το πεδίο {} μπορεί να περιέχει μόνο αλφαριθμητικούς χαρακτήρες',
            alpha_dash: 'Το πεδίο {} μπορεί να περιέχει αλφαριθμητικούς χαρακτήρες καθώς και παύλες και υπογράμμιση.',
            alpha_spaces: 'Το πεδίο {} μπορεί να περιέχει μόνο αλφαβητικούς χαρακτήρες καθώς και κενά.',
            between: 'Το πεδίο " {} " πρέπει να είναι μεταξύ {min} και {max}.',
            confirmed: 'Η επιβεβαίωση του {} πεδίου δεν ταιριάζει',
            digits: 'Το πεδίο {} πρέπει να είναι αριθμητικό και να περιέχει ακριβώς {length} ψηφία',
            dimensions: 'Το πεδίο {} πρέπει να είναι {width} pixels επί {height} pixels',
            email: 'Το πεδίο {} πρέπει να είναι ένα έγκυρο ηλεκτρονικό ταχυδρομείο',
            excluded: 'Το πεδίο {} δεν είναι έγκυρη τιμή',
            ext: 'Το πεδίο {} δεν είναι έγκυρο αρχείο',
            image: 'Το πεδίο {} πρέπει να είναι εικόνα',
            integer: 'Το πεδίο {} πρέπει να είναι ακέραιος αριθμός',
            length: 'Το πεδίο {} πρέπει να είναι {length} long',
            max_value: 'Το πεδίο {} πρέπει να είναι {max} ή λιγότερο',
            max: 'Το πεδίο {} δεν μπορεί να είναι μεγαλύτερο από {length} χαρακτήρες',
            mimes: 'Το πεδίο {} πρέπει να έχει έγκυρο τύπο αρχείου',
            min_value: 'Το πεδίο {} πρέπει να είναι {min} ή περισσότερο',
            min: 'Το πεδίο {} πρέπει να είναι τουλάχιστον χαρακτήρες {length} ',
            numeric: 'Το πεδίο {} μπορεί να περιέχει μόνο αριθμητικούς χαρακτήρες',
            oneOf: 'Το {} πεδίο δεν είναι έγκυρη τιμή',
            regex: 'Η μορφή του πεδίου {} είναι άκυρη',
            required_if: 'Το πεδίο {} είναι υποχρεωτικό',
            required: 'Το πεδίο {} είναι υποχρεωτικό',
            size: 'Το μέγεθος του πεδίου {} πρέπει να είναι μικρότερο από {size}',
            count: {
              length: 'Ο αριθμός των αντικειμένων δεν πρέπει να υπερβαίνει το {length}.',
              isNotArray: 'Το στοιχείο που επικυρώνεται δεν είναι πίνακας ή αντικείμενο.'
            },
            double: 'Το πεδίο {} πρέπει να είναι έγκυρο δεκαδικό'
          }
        }
      },
      da: {
        validations: {
          code: 'da',
          messages: {
            alpha: ' {} må kun indeholde alfabetiske tegn',
            alpha_num: ' {} må kun indeholde alfanumeriske tegn',
            alpha_dash: 'feltet " {} må indeholde alfanumeriske tegn samt bindestreger og understregninger',
            alpha_spaces: ' {} må kun indeholde alfabetiske tegn samt mellemrum',
            between: 'Feltet på {} skal være mellem {min} og {max}',
            confirmed: 'Bekræftelsen af det {} felt stemmer ikke overens',
            digits: ' {} skal være numerisk og indeholde præcis {length} ',
            dimensions: ' {} skal være {width} ved {height} ',
            email: ' {} skal være en gyldig e-mail',
            excluded: ' {} er ikke en gyldig værdi',
            ext: ' {} er ikke en gyldig fil',
            image: 'Det {} felt skal være et billede',
            integer: ' {} skal være et heltal',
            length: 'Feltet {} skal være {length} long',
            max_value: ' {} skal være {max} eller mindre',
            max: ' {} må ikke være større end {length} ',
            mimes: ' {} skal have en gyldig filtype',
            min_value: ' {} skal være {min} eller mere',
            min: ' {} skal være på mindst {length} ',
            numeric: ' {} må kun indeholde numeriske tegn',
            oneOf: 'Feltet " {} " er ikke en gyldig værdi',
            regex: 'Det {} feltformat er ugyldigt',
            required_if: ' {} er påkrævet',
            required: ' {} er påkrævet',
            size: 'Størrelsen af det {} felt skal være mindre end {size}',
            count: {
              length: 'Antallet af objekter bør ikke overstige {length}.',
              isNotArray: 'Elementet, der valideres, er ikke et array eller objekt.'
            },
            double: ' {} skal være et gyldigt decimaltal'
          }
        }
      },
      de: {
        validations: {
          code: 'de',
          messages: {
            alpha: 'Das Feld {} darf nur alphabetische Zeichen enthalten',
            alpha_num: 'Das Feld {} darf nur alphanumerische Zeichen enthalten',
            alpha_dash: 'Das Feld {} darf nur alphanumerische Zeichen sowie Bindestriche und Unterstriche enthalten',
            alpha_spaces: 'Das Feld {} darf nur alphanumerische Zeichen sowie Leerzeichen enthalten',
            between: 'Das {} muss zwischen {min} und {max}liegen',
            confirmed: 'Die Bestätigung des {} stimmt nicht überein',
            digits: 'Das {} muss numerisch sein und genau {length} enthalten',
            dimensions: 'Das {} muss {width} Pixel durch {height} Pixel sein',
            email: 'Das {} muss eine gültige E-Mail sein',
            excluded: 'Das Feld {} ist kein gültiger Wert',
            ext: 'Das Feld {} ist keine gültige Datei',
            image: 'Das {} muss ein Bild sein',
            integer: 'Das {} muss eine ganze Zahl sein',
            length: 'Das {} muss {length} long sein',
            max_value: 'Das Feld {} muss {max} oder weniger sein',
            max: 'Das Feld {} darf nicht mehr als {length} enthalten',
            mimes: 'Das Feld {} muss einen gültigen Dateityp haben',
            min_value: 'Das Feld {} muss {min} oder mehr sein',
            min: 'Das Feld {} muss mindestens {length} enthalten',
            numeric: 'Das {} darf nur numerische Zeichen enthalten',
            oneOf: 'Das Feld {} ist kein gültiger Wert',
            regex: 'Das Format des Feldes {} ist ungültig',
            required_if: 'Das Feld {} ist erforderlich',
            required: 'Das Feld {} ist erforderlich.',
            size: 'Die Größe des Feldes {} muss kleiner als der {size}sein',
            count: {
              length: 'Die Anzahl der Objekte darf {length} nicht überschreiten.',
              isNotArray: 'Das zu validierende Element ist kein Array oder Objekt.'
            },
            double: 'Das Feld {} muss eine gültige Dezimalzahl sein.'
          }
        }
      },
      es: {
        validations: {
          code: 'es',
          messages: {
            alpha: 'Le champ {} ne peut contenir que des caractères alphabétiques',
            alpha_num: 'Le champ {} ne peut contenir que des caractères alphanumériques',
            alpha_dash: 'Le champ {} peut contenir des caractères alphanumériques ainsi que des tirets et des traits de soulignement',
            alpha_spaces: 'Le champ {} ne peut contenir que des caractères alphabétiques ainsi que des espaces',
            between: 'Le champ {} doit être compris entre {min} et {max}',
            confirmed: 'La confirmation du champ {} ne correspond pas',
            digits: 'Le champ {} doit être numérique et contenir exactement des chiffres {length}',
            dimensions: 'Le champ {} doit être {width} pixels par {height} pixels',
            email: 'Le champ {} doit être un e-mail valide',
            excluded: 'Le champ {} n’est pas une valeur valide',
            ext: 'Le champ {} n’est pas un fichier valide',
            image: 'Le champ {} doit être une image',
            integer: 'Le champ {} doit être un entier',
            length: 'Le champ {} doit avoir une longueur {length}',
            max_value: 'Le champ {} doit être {max} ou moins',
            max: 'Le champ {} ne doit pas dépasser les caractères {length}',
            mimes: 'The {} field must have a valid file type',
            min_value: 'The {} field must be {min} or more',
            min: 'The {} field must be at least {length} characters',
            numeric: 'The {} field may only contain numeric characters',
            oneOf: 'Le champ {} n’est pas une valeur valide',
            regex: 'El formato del campo {} no es válido',
            required_if: 'El campo {} es obligatorio',
            required: 'El campo de {} es obligatorio',
            size: 'El tamaño del campo {} debe ser menor que {size}',
            count: {
              length: 'La cantidad de objetos no debe superar {length}.',
              isNotArray: 'El elemento que se está validando no es una matriz ni un objeto.'
            },
            double: 'El campo {} debe ser un decimal válido'
          }
        }
      },
      fi: {
        validations: {
          code: 'fi',
          messages: {
            alpha: '{} -kentässä voi olla vain aakkosmerkkejä',
            alpha_num: 'Kenttä {} voi sisältää vain aakkosnumeerisia merkkejä',
            alpha_dash: 'Kenttä {} voi sisältää aakkosnumeerisia merkkejä sekä katkoviivoja ja alaviivoja',
            alpha_spaces: 'Kenttä {} voi sisältää vain aakkosmerkkejä ja välilyöntejä',
            between: 'Kentän {} on oltava välillä {min} ja {max}',
            confirmed: 'Kentän {} vahvistus ei vastaa toisiaan',
            digits: 'Kentän {} on oltava numeerinen ja sisältää täsmälleen {length}-numeroita',
            dimensions: 'Kentän {} on oltava {width} pikseliä {height}-kuvapisteillä',
            email: 'Kentän {} on oltava kelvollinen sähköpostiviesti',
            excluded: 'Kenttä {} ei ole kelvollinen arvo',
            ext: 'Kenttä {} ei ole kelvollinen tiedosto',
            image: 'Kentän {} on oltava kuva',
            integer: 'Kentän {} on oltava kokonaisluku',
            length: 'Kentän {} on oltava {length} pitkä',
            max_value: 'Kentän {} on oltava {max} tai vähemmän',
            max: 'Kenttä {} ei saa olla suurempi kuin {length}- merkkinen',
            mimes: 'The {} field must have a valid file type',
            min_value: 'The {} field must be {min} or more',
            min: 'The {} field must be at least {length} characters',
            numeric: 'The {} field may only contain numeric characters',
            oneOf: 'Kenttä {} ei ole kelvollinen arvo',
            regex: ' {} muoto on virheellinen',
            required_if: ' {} on pakollinen',
            required: ' {} kenttä vaaditaan',
            size: ' {} koon on oltava pienempi kuin {size}',
            count: {
              length: 'Objektien lukumäärä ei saa ylittää {length}.\n',
              isNotArray: 'Vahvistettava elementti ei ole taulukko tai objekti.'
            },
            double: ' {} on oltava kelvollinen desimaaliluku.'
          }
        }
      },
      fr: {
        validations: {
          code: 'fr',
          messages: {
            alpha: 'Le champ {} ne peut contenir que des caractères alphabétiques',
            alpha_num: 'Le champ {} ne peut contenir que des caractères alphanumériques.',
            alpha_dash: 'Le champ " {} peut contenir des caractères alphanumériques ainsi que des tirets et des tirets bas.',
            alpha_spaces: 'Le champ {} ne peut contenir que des caractères alphabétiques et des espaces.',
            between: 'Le champ {} doit être compris entre {min} et {max}',
            confirmed: 'La confirmation du champ {} ne correspond pas',
            digits: 'Le champ {} doit être numérique et contenir exactement les chiffres de {length} ',
            dimensions: 'Le champ {} doit avoir une taille de pixels {width} par pixels de {height}.',
            email: 'Le champ {} doit être une adresse électronique valide.',
            excluded: 'Le champ {} n\'est pas une valeur valide',
            ext: 'Le champ {} n\'est pas un fichier valide',
            image: 'Le champ {} doit être une image',
            integer: 'Le champ {} doit être un nombre entier',
            length: 'Le champ {} doit être {length} long',
            max_value: 'Le champ de {} doit être inférieur ou égal à {max} ',
            max: 'Le champ {} ne doit pas dépasser {length} caractères.',
            mimes: 'Le champ {} doit avoir un type de fichier valide',
            min_value: 'Le champ {} doit être de {min} ou plus',
            min: 'Le champ {} doit comporter au moins {length} caractères',
            numeric: 'Le champ {} ne peut contenir que des caractères numériques.',
            oneOf: 'Le champ {} n\'est pas une valeur valide',
            regex: 'Le format du champ {} n\'est pas valide',
            required_if: 'Le champ {} est obligatoire',
            required: 'Le champ {} est obligatoire',
            size: 'La taille du champ {} doit être inférieure à {size}',
            count: {
              length: 'Le nombre d\'objets ne doit pas dépasser {length}.',
              isNotArray: 'L\'élément en cours de validation n\'est ni un tableau ni un objet.'
            },
            double: 'Le champ {} doit être une valeur décimale valide.'
          }
        }
      },
      hr: {
        validations: {
          code: 'hr',
          messages: {
            alpha: 'Polje {} smije sadržavati samo abecedne znakove',
            alpha_num: ' Polje {} smije sadržavati samo alfanumeričke znakove',
            alpha_dash: ' Polje {} može sadržavati alfanumeričke znakove, kao i crtice i podvlake',
            alpha_spaces: ' Polje za {} može sadržavati samo abecedne znakove kao i razmake',
            between: ' Polje {} mora biti između {min} i {max}',
            confirmed: ' Potvrda {} polja ne odgovara',
            digits: ' Polje za {} mora biti brojčano i točno sadržavati {length} znamenke',
            dimensions: ' {} polje mora biti {width} pixels by {height} pixels',
            email: ' Polje {} mora biti valjana e-pošta',
            excluded: ' Polje za {} nije valjana vrijednost',
            ext: ' {} polje nije važeća datoteka',
            image: ' {} polje mora biti slika',
            integer: ' Polje {} mora biti cijeli broj',
            length: ' Polje {} mora biti dugo {length}',
            max_value: ' {} polje mora biti {max} ili manje',
            max: ' Polje {} ne smije biti veće od znakova {length}',
            mimes: ' Polje {} mora imati važeću vrstu datoteke',
            min_value: ' {} polje mora biti {min} ili više',
            min: ' {} polje mora sadržavati najmanje {length} znakove',
            numeric: ' {} polje može sadržavati samo numeričke znakove',
            oneOf: ' Polje {} nije važeća vrijednost',
            regex: ' Format polja {} nije važeći',
            required_if: ' {} polje je obavezno',
            required: ' {} polje je obavezno',
            size: ' Veličina polja {} mora biti manja od {size}',
            count: {
              length: 'Broj objekata ne smije premašiti {length}.',
              isNotArray: 'Element koji se provjerava nije niz ili objekt.'
            },
            double: ' Polje {} mora biti valjana decimala'
          }
        }
      },
      it: {
        validations: {
          code: 'it',
          messages: {
            alpha: 'Il campo {} può contenere solo caratteri alfabetici',
            alpha_num: 'Il campo {} può contenere solo caratteri alfanumerici',
            alpha_dash: 'Il campo {} può contenere caratteri alfanumerici oltre a trattini e underscore',
            alpha_spaces: 'Il campo {} può contenere solo caratteri alfabetici e spazi',
            between: 'Il campo {} deve essere compreso tra {min} e {max}',
            confirmed: 'La conferma del campo {} non corrisponde',
            digits: 'Il campo {} deve essere numerico e contenere esattamente {length} di cifre',
            dimensions: 'Il campo {} deve essere {width} pixel per {height} pixel',
            email: 'Il campo {} deve essere un\'email valida',
            excluded: 'Il campo {} non è un valore valido',
            ext: 'Il campo {} non è un file valido',
            image: 'Il campo {} deve essere un\'immagine',
            integer: 'Il campo {} deve essere un intero',
            length: 'Il campo {} deve essere {length} lungo',
            max_value: 'Il campo {} deve essere {max} o meno',
            max: 'Il campo {} non deve essere maggiore di {length} caratteri',
            mimes: 'Il campo {} deve avere un tipo di file valido',
            min_value: 'Il campo {} deve essere {min} o più',
            min: 'Il campo {} deve essere di almeno caratteri {length} ',
            numeric: 'Il campo {} può contenere solo caratteri numerici',
            oneOf: 'Il campo {} non è un valore valido',
            regex: 'Il formato del campo {} non è valido',
            required_if: 'Il campo {} è richiesto',
            required: 'Il campo {} è richiesto',
            size: 'La dimensione del campo {} deve essere inferiore a {size}',
            count: {
              length: 'Il numero di oggetti non deve superare {length}.',
              isNotArray: 'L\'elemento convalidato non è un array o un oggetto.'
            },
            double: 'Il campo {} deve essere un decimale valido'
          }
        }
      },
      nl: {
        validations: {
          code: 'nl',
          messages: {
            alpha: 'Het {} mag alleen alfabetische tekens bevatten',
            alpha_num: 'Het veld {} mag alleen alfanumerieke tekens bevatten',
            alpha_dash: 'Het veld {} mag naast alfanumerieke tekens ook streepjes en onderstrepingen bevatten',
            alpha_spaces: 'Het {} veld mag alleen alfabetische tekens en spaties bevatten',
            between: 'Het {} veld moet tussen {min} en {max}zijn',
            confirmed: 'De bevestiging van het {} komt niet overeen',
            digits: 'Het {} veld moet numeriek zijn en precies {length} cijfers bevatten',
            dimensions: 'Het veld {} moet {width} pixels bij {height} pixels zijn',
            email: 'Het {} veld moet een geldig e-mail zijn',
            excluded: 'Het {} veld is geen geldige waarde',
            ext: 'Het {} veld is geen geldig bestand',
            image: 'Het {} veld moet een beeld zijn',
            integer: 'Het {} veld moet een geheel getal zijn',
            length: 'Het {} moet {length} lang zijn',
            max_value: 'Het {} veld moet {max} of minder zijn',
            max: 'Het {} veld mag niet groter zijn dan {length} karakters',
            mimes: 'Het {} veld moet een geldig bestandstype hebben',
            min_value: 'Het {} veld moet {min} of meer zijn',
            min: 'Het {} moet ten minste {length} bevatten',
            numeric: 'Het {} veld mag alleen numerieke tekens bevatten',
            oneOf: 'Het veld {} is geen geldige waarde',
            regex: 'Het {} is ongeldig',
            required_if: 'Het veld {} is verplicht',
            required: 'Het veld {} is verplicht',
            size: 'De veldgrootte {} moet kleiner zijn dan {size}',
            count: {
              length: 'Het aantal objecten mag niet groter zijn dan {length}.',
              isNotArray: 'Het element dat gevalideerd wordt, is geen array of object.'
            },
            double: 'Het veld {} moet een geldig decimaalteken zijn'
          }
        }
      },
      pl: {
        validations: {
          code: 'pl',
          messages: {
            alpha: 'Pole {} może zawierać wyłącznie znaki alfabetyczne',
            alpha_num: 'Pole {} może zawierać wyłącznie znaki alfanumeryczne.',
            alpha_dash: 'Pole {} może zawierać wyłącznie znaki alfanumeryczne, a także myślniki i podkreślenia',
            alpha_spaces: 'Pole {} może zawierać wyłącznie znaki alfabetyczne oraz spacje.',
            between: 'Pole {} musi mieć wartość od {min} do {max}.',
            confirmed: 'Potwierdzenie pola {} nie jest zgodne',
            digits: 'Pole {} musi być numeryczne i zawierać dokładnie {length} cyfry',
            dimensions: 'Pole {} musi mieć rozmiar {width} pikseli na piksele skierowane do {height} ',
            email: 'Pole {} musi być poprawnym e-mailem',
            excluded: 'Pole {} nie jest prawidłową wartością',
            ext: 'Pole {} nie jest prawidłowym plikiem',
            image: 'Pole {} musi być obrazem',
            integer: 'Pole {} musi być liczbą całkowitą',
            length: 'Pole {} musi być długością {length} ',
            max_value: 'Pole {} musi mieć wartość {max} lub mniejszą',
            max: 'Pole {} nie może być większe niż {length} characters.',
            mimes: 'Pole {} musi mieć prawidłowy typ pliku.',
            min_value: 'Pole {} musi mieć wartość {min} lub większą',
            min: 'Pole {} musi mieć długość co najmniej znaków {length} ',
            numeric: 'Pole {} może zawierać tylko znaki numeryczne.',
            oneOf: 'Pole  {}  nie jest prawidłową wartością',
            regex: 'Format pola {} jest nieprawidłowy.',
            required_if: 'Pole {} jest wymagane',
            required: 'Pole {} jest wymagane',
            size: 'Rozmiar pola {} musi być mniejszy niż {size}',
            count: {
              length: 'Liczba obiektów nie powinna przekraczać {length}.',
              isNotArray: 'Element poddawany walidacji nie jest tablicą ani obiektem.'
            },
            double: 'Pole {} musi być poprawną wartością dziesiętną.'
          }
        }
      },
      pt: {
        validations: {
          code: 'pt',
          messages: {
            alpha: 'O campo da {} só pode conter caracteres alfabéticos',
            alpha_num: 'O campo {} só pode conter caracteres alfanuméricos',
            alpha_dash: 'O campo da {} pode conter caracteres alfanuméricos, bem como traços e sublinhados',
            alpha_spaces: 'O campo {} só pode conter caracteres alfabéticos, bem como espaços',
            between: 'O campo {} deve estar entre a {min} e {max}',
            confirmed: 'A confirmação de campo {} não coincide',
            digits: 'O campo {} deve ser numérico e conter exactamente os dígitos {length} ',
            dimensions: 'O campo de {} deve ser {width} por pixéis {height} ',
            email: 'O campo {} deve ser um e-mail válido',
            excluded: 'O campo {} não é um valor válido',
            ext: 'O campo {} não é um ficheiro válido',
            image: 'O campo do {} deve ser uma imagem',
            integer: 'O campo {} deve ser um número inteiro',
            length: 'O campo do {} deve ser longo {length} ',
            max_value: 'O campo de {} deve ser de {max} ou menos',
            max: 'O campo de {} não pode ser maior que os caracteres de {length} ',
            mimes: 'O campo {} deve ter um tipo de ficheiro válido',
            min_value: 'O campo {} deve ser {min} ou mais',
            min: 'O campo dos {} deve ser pelo menos caracteres de {length} ',
            numeric: 'O campo do {} só pode conter caracteres numéricos',
            oneOf: 'O campo {} não é um valor válido',
            regex: 'O formato do campo de {} é inválido',
            required_if: 'O campo de {} é necessário',
            required: 'O campo de {} é necessário',
            size: 'O tamanho do campo do {} deve ser inferior a {size}',
            count: {
              length: 'O número de objetos não deve exceder {length}.',
              isNotArray: 'O elemento que está sendo validado não é um array ou objeto.'
            },
            double: 'O campo {} deve ser uma casa decimal válida'
          }
        }
      },
      sv: {
        validations: {
          code: 'sv',
          messages: {
            alpha: ' {} får endast innehålla alfabetiska tecken.',
            alpha_num: 'Fältet {} får endast innehålla alfanumeriska tecken.',
            alpha_dash: 'Det {} fältet får innehå•lla alfanumeriska tecken samt bindestreck och understrykningar.',
            alpha_spaces: 'Fältet {} får endast innehålla alfabetiska tecken samt mellanslag.',
            between: ' {} måste vara mellan {min} och {max}.',
            confirmed: 'Bekräftelsen för fältet {} stämmer inte överens med',
            digits: 'Det {} fältet måste vara numeriskt och innehålla exakt {length}.',
            dimensions: 'Det {} fältet måste vara {width} pixlar och {height} pixlar.',
            email: 'Det {} fältet måste vara en giltig e-postadress.',
            excluded: 'Det {} fältet är inte ett giltigt värde.',
            ext: 'Det {} fältet är inte en giltig fil.',
            image: ' {} måste vara en bild.',
            integer: 'Fältet {} måste vara ett heltal.',
            length: 'Det {} fältet måste vara {length} long.',
            max_value: 'Fältet för {} måste vara {max} eller mindre.',
            max: 'Fältet {} får inte vara större än {length}.',
            mimes: 'Fältet {} måste ha en giltig filtyp.',
            min_value: 'Fältet {} måste vara {min} eller mer.',
            min: ' {} måste innehålla minst {length}.',
            numeric: 'Fältet {} får endast innehålla numeriska tecken.',
            oneOf: 'Fältet {} är inte ett giltigt värde.',
            regex: 'Fältformatet {} är ogiltigt.',
            required_if: 'Fältet  {} är obligatoriskt.',
            required: 'Fältet {} är obligatoriskt.',
            size: ' {} storlek måste vara mindre än {size}',
            count: {
              length: 'Antalet objekt får inte överstiga {length}.',
              isNotArray: 'Elementet som valideras är inte en array eller objekt.'
            },
            double: 'Fältet {} måste vara en giltig decimal.'
          }
        }
      }
    }
  })

  extend('count', {
    validate (value, args) {
      if (!(Array.isArray(value) || value instanceof Object)) {
        args.msg = 'validations.messages.count.isNotArray'

        return false
      }
      if (value.length > args.length) {
        args.msg = 'validations.messages.count.length'

        return false
      }

      return true
    },
    params: ['length', 'msg', ],
    message (_, values) {
      return i18n.t(values.msg, values)
    }
  })
  extend('image', {
    ...image,
    message: (_, values) => {
      return i18n.t('validations.messages.image', values)
    }
  })
  extend('email', {
    ...email,
    message: (_, values) => {
      return i18n.t('validations.messages.email', values)
    }
  })
  extend('phone', {
    validate (value, args) {
      value = value?.replaceAll(' ', '')
      value = value?.replace('+', '')
      if (args?.format) {
        value = value?.replace(args.format, '')
      }
      const re = /\(?([0-9]{3})\)?([ .-]?)([0-9]{2})\2([0-9]{2})/
      return !!re.test(`${args.format ? args.format : ''}${value}`)
    },
    params: ['format'],
    message: (_, values) => {
      return i18n.t('validations.messages.phone', values)
    }
  })
  extend('required', {
    ...required,
    message: (_, values) => {
      return i18n.t('validations.messages.required', values)
    }
  })
  extend('size', {
    ...size,
    params: size.params,
    message: (_, values) => {
      return i18n.t('validations.messages.size', { size: formatKB(values.size, 2) })
    }
  })
  extend('min', {
    ...min,
    message (_, values) {
      return i18n.t('validations.messages.min', values)
    }
  })
  extend('password', {
    validate (value, args) {
      args.length = 8
      if (value.length < args.length) {
        args.msg = 'validations.messages.password.length'
        return false
      }
      if (value.search(/[a-z]/i) < 0) {
        args.msg = 'validations.messages.password.letters'
        return false
      }
      if (value.search(/[0-9]/) < 0) {
        args.msg = 'validations.messages.password.digit'
        return false
      }
      return true
    },
    params: ['msg', 'length'],
    message (_, values) {
      return i18n.t(values.msg, values)
    }
  })
  extend('max', {
    ...max,
    message (_, values) {
      return i18n.t('validations.messages.max', values)
    }
  })
  extend('min_value', {
    ...min_value,
    params: min_value.params,
    message (_, values) {
      return i18n.t('validations.messages.min_value', values)
    }
  })
  extend('max_value', {
    ...max_value,
    params: max_value.params,
    message (_, values) {
      return i18n.t('validations.messages.max_value', values)
    }
  })
  extend('list', {
    validate (value, args) {
      if (isObject(value) && value.id) {
        return true
      } else if (!isObject(value) && value) {
        return true
      }
    },
    message (_, values) {
      return i18n.t(values.msg, values)
    }
  })
}
